import { cx } from '@emotion/css';
import { Alignment, Block as BlockSDS } from '@snapchat/snap-design-system-marketing';
import type { FC } from 'react';
import { useCallback, useState } from 'react';

import type { ContentfulIdVariable } from '../../../../hooks/useContentfulQuery';
import { useContentfulQuery } from '../../../../hooks/useContentfulQuery';
import type { ContentfulIdProps } from '../../../../types/contentful';
import { parseMedia } from '../../../../utils/media';
import { renderRichTextMarkingsOnly } from '../../../../utils/renderText/renderRichText';
import { customRenderParagraph } from '../../helpers';
import { ArDownloadForm } from './ArDownloadForm';
import {
  alignCenterCss,
  alignRightCss,
  blockMarginBottonCss,
  blockWrapperCss,
  contentCss,
  contentHeightBlockDenseCss,
  contentHeightBlockSpaciousCss,
  contentResetPaddingCss,
  logoCss,
} from './ArDownloadFormBlock.styles';
import type { ArDownloadFormBlockQuery } from './arDownloadFormBlockQuery';
import { query } from './arDownloadFormBlockQuery';
import { ArDownloadFormHeader } from './ArDownloadFormHeader';
import { ArDownloadSuccess } from './ArDownloadSuccess';
import { ArDownloadFormSpacing, ArDownloadFormStyle } from './types';

type ArDownloadFormProps = ContentfulIdProps & {
  id: string;
};

export const ArDownloadFormBlock: FC<ArDownloadFormProps> = ({ id }) => {
  const [downloadUrl, setDownloadUrl] = useState<string | undefined>(undefined);

  const { data } = useContentfulQuery<ArDownloadFormBlockQuery, ContentfulIdVariable>(query, {
    variables: { id },
  });

  const onDownloadSuccess = useCallback((downloadUrl?: string) => {
    setDownloadUrl(downloadUrl);
  }, []);

  if (!data?.arDownloadFormBlock) {
    return null;
  }

  const {
    backgroundColor,
    alignment,
    style,
    spacing,
    title: titleRichText,
    subtitle: subtitleRichText,
    backgroundMedia,
    logo,
    endpoint,
    analytics,
    submitText,
    submitSuccessText,
    successTitle: successTitleRichText,
    successText: successTextRichText,
    version,
    platform,
    eula,
  } = data.arDownloadFormBlock;

  const title = renderRichTextMarkingsOnly(titleRichText);
  const subtitle = renderRichTextMarkingsOnly(subtitleRichText);
  const successTitle = renderRichTextMarkingsOnly(successTitleRichText);
  const successText = customRenderParagraph(successTextRichText);

  const { imageSource: backgroundImageSource } = parseMedia(backgroundMedia);
  const { imageSource: logoImageSource, imageAltText: logoAltText } = parseMedia(logo);

  const downloadSuccess = downloadUrl !== undefined && downloadUrl !== null;

  return (
    <div
      className={cx('ar-download-from-container', blockWrapperCss, {
        [contentResetPaddingCss]: style === ArDownloadFormStyle.Block,
        [blockMarginBottonCss]: style !== ArDownloadFormStyle.Block,
      })}
    >
      <BlockSDS
        backgroundColor={backgroundColor}
        style={{ backgroundImage: `url("${backgroundImageSource}")` }}
      >
        <div
          className={cx('ar-dowload-form-content', contentCss, {
            [alignRightCss]: alignment === Alignment.Right,
            [alignCenterCss]: alignment === Alignment.Center,
            [contentHeightBlockDenseCss]:
              style === ArDownloadFormStyle.Block && spacing !== ArDownloadFormSpacing.Spacious,
            [contentHeightBlockSpaciousCss]:
              style === ArDownloadFormStyle.Block && spacing === ArDownloadFormSpacing.Spacious,
          })}
        >
          <img className={logoCss} src={logoImageSource} alt={logoAltText} />
          <ArDownloadFormHeader
            title={downloadSuccess ? successTitle : title}
            subtitle={downloadSuccess ? null : subtitle}
            titleStyle={style === ArDownloadFormStyle.Block ? 'H200' : 'H300'}
            subtitleStyle="H400"
            alignment={alignment}
          />
          {downloadSuccess ? (
            <ArDownloadSuccess successText={successText} downloadUrl={downloadUrl} />
          ) : (
            <ArDownloadForm
              id={id}
              formStyle={style}
              alignment={alignment}
              analytics={analytics}
              endpoint={endpoint}
              version={version}
              eula={eula}
              platform={platform}
              submitText={submitText}
              submitSuccessText={submitSuccessText}
              onDownloadSuccess={onDownloadSuccess}
            />
          )}
        </div>
      </BlockSDS>
    </div>
  );
};
